import { Link } from "react-router-dom";
import { optimizeImage, optimizeImageSrcSet } from '@/lib/imageOptimization';
import { cn } from "@/lib/utils";
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';
import { useContentHover } from '@/hooks/useContentHover';
import { useSmartImage } from '@/hooks/useSmartImage';

interface LearnArticleCardProps {
  slug: string;
  title: string;
  coverImage: string | null;
  topicName?: string;
  topicColor?: string | null;
  variant?: "featured" | "feed";
  layout?: "carousel" | "grid";
  /** ⚠️ ANALYTICS: Position index for tracking */
  index?: number;
  /** ⚠️ ANALYTICS: Section ID for tracking */
  sectionId?: string;
}

/**
 * component-card-article-featured / component-card-article-feed
 * 
 * Featured variant: Tag pill above title (outside image), no author
 * Feed variant: No tag, no author
 * 
 * Layout modes:
 * - carousel: Fixed w-[435px] for horizontal scrolling carousels
 * - grid: Responsive w-full for CSS grid layouts
 * 
 * Tokens applied:
 * - Image: w-[435px] h-[435px] (carousel) or w-full aspect-square (grid), rounded-3xl (primitive-radius-3xl)
 * - Title: text-28 font-bold text-carbon pr-6 min-h-[70px] line-clamp-2 (atom-text-article-title)
 *   - min-h-[70px] ensures consistent card height for carousel nav alignment
 *   - Accommodates up to 2 lines of text-28 (28px × 1.25 line-height × 2 lines)
 * - Tag (featured only): CMS-managed color with white text (atom-tag-topic)
 * - Mobile carousel: calc(100vw - 3.5rem) width to peek next card into view
 */
export default function LearnArticleCard({
  slug,
  title,
  coverImage,
  topicName,
  topicColor,
  variant = "feed",
  layout = "carousel",
  index = 0,
  sectionId = 'learn',
}: LearnArticleCardProps) {
  const isCarousel = layout === "carousel";
  const { ref, inView } = useSmartImage();

  // ⚠️ ANALYTICS: Track article card hover
  const { contentHoverProps } = useContentHover({
    contentId: slug,
    contentType: 'card',
    contentTitle: title,
    contentSection: sectionId,
    contentPage: 'learn',
    contentPosition: index,
  });

  // ⚠️ ANALYTICS: Track article card click
  const handleArticleClick = () => {
    trackEvent(ANALYTICS_EVENTS.CARD_CLICKED, {
      card_id: slug,
      card_title: title,
      card_type: 'article',
      card_section: sectionId,
      card_topic: topicName || 'unknown',
      card_variant: variant,
      click_action: 'navigate',
      destination: `/blog/${slug}`,
    });
  };
  
  return (
    <Link 
      to={`/blog/${slug}`}
      onClick={handleArticleClick}
      {...contentHoverProps}
      className={cn(
        "group block",
        // Mobile: slightly narrower to peek next card; Desktop carousel: fixed 435px
        isCarousel ? "w-[calc(100vw-3.5rem)] max-w-[435px] md:w-[435px] flex-shrink-0" : "w-full"
      )}
    >
      <article className="cursor-pointer">
        {/* Square Image - responsive on mobile, fixed on desktop */}
        <div 
          ref={ref}
          className={cn(
            "overflow-hidden rounded-3xl mb-4",
            isCarousel ? "w-full aspect-square md:w-[435px] md:h-[435px]" : "w-full aspect-square"
          )}
        >
          {inView && coverImage ? (
            <img
              src={optimizeImage(coverImage, { width: 435 })}
              srcSet={optimizeImageSrcSet(coverImage, [300, 435, 600, 870])}
              sizes="(max-width: 435px) 100vw, 435px"
              alt={title}
              loading="lazy"
              decoding="async"
              width={435}
              height={435}
              className="w-full h-full object-cover transition-transform duration-500 md:group-hover:scale-105"
            />
          ) : !coverImage ? (
            <div className="w-full h-full bg-muted" />
          ) : null}
        </div>

        {/* atom-tag-custom: Topic Badge - Only for Featured variant, outside image */}
        {variant === "featured" && topicName && (
          <span 
            className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase mt-2 mb-3"
            style={{ 
              backgroundColor: topicColor || '#E8E8ED',
              color: '#FFFFFF' // primitive-color-white
            }}
          >
            {topicName}
          </span>
        )}

        {/* atom-text-article-title: Title - min-h ensures consistent card height for nav alignment */}
        <h3 className="text-21 md:text-28 font-bold text-carbon leading-tight line-clamp-2 pr-4 md:pr-6 min-h-[52px] md:min-h-[70px]">
          {title}
        </h3>
      </article>
    </Link>
  );
}
